import React, { useState } from 'react';
import { storeInfo } from '../data/mockData';
import { Phone, MessageCircle, Mail, Instagram, Facebook, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', contact: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: storeInfo.contact.email,
          contact: formData.contact,
          message: formData.message,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
        setStatus({ type: 'success', message: 'Message sent! We\'ll be in touch soon.' });
        setFormData({ name: '', contact: '', message: '' });
        setTimeout(() => { setSubmitted(false); setStatus({ type: '', message: '' }); }, 4000);
      } else {
        setStatus({ type: 'error', message: data.detail || 'Something went wrong. Please try again.' });
      }
    } catch {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
          <span className="text-white">Contact &amp; </span>
          <span className="text-[#c8ff00]">Connect</span>
        </h2>
        <p className="text-gray-400 text-center mb-12">
          Get in touch with us. We're here to help with any questions about our products or services.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Left - Contact Info */}
          <div>
            <h3 className="text-white text-xl font-bold mb-6">Get In Touch</h3>
            <div className="space-y-4 mb-8">
              <a
                href={storeInfo.contact.phoneLink}
                className="flex items-center gap-4 bg-[#111] border border-[#222] rounded-xl p-4 hover:border-[#c8ff00]/30 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-full bg-[#c8ff00]/10 flex items-center justify-center">
                  <Phone size={18} className="text-[#c8ff00]" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Call Us</p>
                  <p className="text-gray-400 text-sm">{storeInfo.contact.phone}</p>
                </div>
              </a>

              <a
                href={storeInfo.contact.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-[#111] border border-[#222] rounded-xl p-4 hover:border-[#c8ff00]/30 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-full bg-[#c8ff00]/10 flex items-center justify-center">
                  <MessageCircle size={18} className="text-[#c8ff00]" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">WhatsApp</p>
                  <p className="text-gray-400 text-sm">Chat with us</p>
                </div>
              </a>

              <a
                href={`mailto:${storeInfo.contact.email}`}
                className="flex items-center gap-4 bg-[#111] border border-[#222] rounded-xl p-4 hover:border-[#c8ff00]/30 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-full bg-[#c8ff00]/10 flex items-center justify-center">
                  <Mail size={18} className="text-[#c8ff00]" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Email</p>
                  <p className="text-gray-400 text-sm">{storeInfo.contact.email}</p>
                </div>
              </a>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="text-white font-semibold mb-3">Follow Us</h4>
              <div className="flex gap-3">
                <a
                  href={storeInfo.contact.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#111] border border-[#222] flex items-center justify-center text-gray-400 hover:text-[#c8ff00] hover:border-[#c8ff00]/30 transition-all duration-300"
                >
                  <Instagram size={18} />
                </a>
                <a
                  href={storeInfo.contact.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#111] border border-[#222] flex items-center justify-center text-gray-400 hover:text-[#c8ff00] hover:border-[#c8ff00]/30 transition-all duration-300"
                >
                  <Facebook size={18} />
                </a>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div>
            <h3 className="text-white text-xl font-bold mb-6">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-[#111] border border-[#222] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#c8ff00]/50 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Phone or Email</label>
                <input
                  type="text"
                  value={formData.contact}
                  onChange={(e) => setFormData({...formData, contact: e.target.value})}
                  className="w-full bg-[#111] border border-[#222] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#c8ff00]/50 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows={5}
                  className="w-full bg-[#111] border border-[#222] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#c8ff00]/50 transition-colors resize-none"
                  required
                />
              </div>
              {status.message && (
                <p className={`text-sm text-center py-2 rounded-xl ${status.type === 'success' ? 'text-[#c8ff00]' : 'text-red-400'}`}>
                  {status.message}
                </p>
              )}
              <button
                type="submit"
                disabled={loading || submitted}
                className="w-full bg-[#c8ff00] text-black font-semibold py-3 rounded-xl hover:bg-[#b8ef00] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? 'Sending...' : submitted ? 'Message Sent!' : <><Send size={16} /> Send Message</>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
